import React from 'react';
import classNames from 'classnames';
import { AsyncTypeahead, Input } from 'react-bootstrap-typeahead';
import type { RenderMenuItemChildren } from 'react-bootstrap-typeahead';
import type { Option, TypeaheadInputProps } from 'react-bootstrap-typeahead/types/types';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-bootstrap-typeahead/css/Typeahead.css';

// react-bootstrap-typeahead 6.x dejó de tipar sus componentes de forma
// genérica: `Option`/`LabelKey`/`RenderMenuItemChildren` ahora son fijos
// (`Option = string | Record<string, any>`), no más `TypeaheadModel<T>`.
// Esta app siempre usa objetos reales tipados (nunca free-text, `allowNew`
// queda en `false`), así que se preserva la firma genérica propia del
// wrapper y se castea sólo en el borde donde se le pasa a la librería.
interface Props<T> {
  htmlId?: string;
  list: T[] | null;
  text?: string | React.ReactNode;
  placeholder?: string;
  labelKey?: string;
  loading: boolean;
  onSearch: (query: string) => void;
  onChange?: (selected: T[]) => void;
  onBlur?: () => void;
  render?: (option: T) => React.ReactNode;
  className?: string;
  multiple?: boolean;
  selected?: T[];
  disabled?: boolean;
}

// Input de selección simple SIN la sugerencia en gris dentro del campo ("hint": el resto del
// primer resultado, p.ej. "luc|a Marini"). react-bootstrap-typeahead 6.x envuelve siempre el
// input simple en <Hint> (TypeaheadInputSingle) y no tiene prop para apagarlo; esto replica
// TypeaheadInputSingle sin ese envoltorio. El de selección múltiple (chips) no muestra hint.
// `inputClassName` se descarta: es solo para el input multiple y no es un atributo del DOM.
const renderInputWithoutHint = ({
  inputRef,
  referenceElementRef,
  className,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- se descarta a propósito
  inputClassName,
  ...inputProps
}: TypeaheadInputProps) => (
  <Input
    {...inputProps}
    className={classNames('form-control', 'rbt-input', className)}
    ref={(node: HTMLInputElement | null) => {
      inputRef(node);
      referenceElementRef(node);
    }}
  />
);

const AutocompleteChips = <T extends object>(props: Props<T>) => {
  const {
    htmlId,
    list,
    text,
    placeholder,
    labelKey,
    onSearch,
    onChange,
    loading,
    render,
    className,
    multiple,
    selected,
    onBlur,
    disabled = false,
  } = props;

  return (
    <>
      {text && (
        <label htmlFor={htmlId} className={className}>
          {text}
        </label>
      )}
      <AsyncTypeahead
        id={htmlId}
        labelKey={labelKey}
        options={(list || []) as unknown as Option[]}
        placeholder={placeholder}
        multiple={multiple}
        minLength={3}
        onSearch={onSearch}
        onChange={onChange as unknown as ((selected: Option[]) => void) | undefined}
        onBlur={onBlur}
        allowNew={false}
        isLoading={loading}
        renderMenuItemChildren={render as unknown as RenderMenuItemChildren | undefined}
        useCache={false}
        selected={selected as unknown as Option[] | undefined}
        disabled={disabled}
        renderInput={multiple ? undefined : renderInputWithoutHint}
      />
    </>
  );
};

export default AutocompleteChips;
